// ============================================================================
// advisorDraft.js — s169 · persistencia de propuestas del asesor IA
// Tickets: asesor-ia-no-persiste-lo-generado · asesor-seleccion-multiple (s159-D3)
//          + el fix ratificado de s132 (el alta descartaba advisor_json)
// ============================================================================
//
// POR QUÉ EXISTE ESTE ARCHIVO Y NO ES CÓDIGO DENTRO DE App.jsx:
// el flujo de alta sin fricción (SPEC_s148 CABLE 2 + §3.3) va a leer y escribir
// estas propuestas desde OTRO lugar — al aterrizar del registro, no al apretar
// «Generar». Por eso leer/guardar no puede depender de cómo está dibujada la
// pantalla del carril IA. Esta capa es la que ese flujo va a reusar.
//
// EL PROBLEMA QUE RESUELVE:
// el asesor es un recurso con cupo (10 generaciones por comercio). Hasta s169 el
// front generaba N propuestas, el comercio usaba 1, y las otras N-1 se perdían
// con el cupo ya gastado. Acá las marcadas sobreviven al unmount.
//
// DOS BACKENDS, REMOTO GANA:
//   - remoto  = MerchantPartner.last_advisor_json (cross-device, el que importa)
//   - local   = localStorage (mismo navegador y mismo dispositivo, nada más)
//
// 🔴 EL BACKEND LOCAL ESTÁ CONSTRUIDO Y APAGADO A PROPÓSITO. No lo enciendas sin
// hablarlo. La razón (decidida en s169): el comercio arranca en el teléfono y
// sigue en la computadora — ese es el caso más probable, no el menos. Prender
// solo el local prometería una persistencia que no cumple, y el cartel honesto
// («guardado en este dispositivo») caería en el peor momento: justo después de
// que el comercio entregó su web y cuatro respuestas.
// Se enciende SOLO como respaldo del remoto, nunca en su lugar.

import { parseAdvisorRaw, saveAdvisorDraft } from "./api";

// Prender esto sin el campo de Bubble es exactamente lo que s169 decidió NO hacer.
export const LOCAL_BACKEND_ENABLED = false;

// ============================================================================
// 🔴 TECHO DE PROPUESTAS GUARDADAS — el número está acá y tiene una razón.
// ============================================================================
// NO es el límite de un campo `text` de Bubble: ese aguanta muchísimo más.
// El motivo real es de red, y se paga en un lugar que no es esta pantalla:
//
//   `last_advisor_json` viaja en CADA respuesta de `get_merchant_profile`,
//   y ese WF lo llama el panel del comercio todo el tiempo.
//
// Una propuesta pesa ~1–1.2 KB (el `why_it_works` del asesor son dos o tres
// frases, y va en cada tarjeta). Doce propuestas son 12–15 KB colgados de cada
// carga del panel — y como el merge acumula las marcadas, sin techo solo crece.
//
// 8 es el número: cubre las marcadas de dos generaciones más una tanda nueva de
// 3-4, y deja el peor caso en ~10 KB. Si alguna vez hay que subirlo, la pregunta
// no es "¿entra en el campo?" sino "¿cuánto quiero que pese cada carga del panel
// del comercio?". Subir esto sin mirar eso es empeorar el panel para arreglar
// esta pantalla.
//
// La otra palanca, si el peso vuelve a molestar: recortar `_adv.why_it_works` al
// guardar. NO se hizo, a propósito — es justo lo que hace útil a la tarjeta
// rehidratada, y una tarjeta sin el porqué es una tarjeta que el comercio no
// entiende tres días después.
export const MAX_SAVED_PROPOSALS = 8;

/** Aplica el techo: lo que viene primero se queda. Nunca lanza. */
function capProposals(offers = []) {
  return offers.length > MAX_SAVED_PROPOSALS ? offers.slice(0, MAX_SAVED_PROPOSALS) : offers;
}

const ENVELOPE_TAG = "nxtr_advisor_draft";
const ENVELOPE_VERSION = 1;
const LOCAL_PREFIX = "nxtr.advisorDraft.";

// ---------------------------------------------------------------- identidad
// Clave estable por propuesta. El índice del array NO sirve: al fusionar
// generaciones las posiciones se corren y la selección apuntaría a otra tarjeta.
// Se arma con lo que identifica la propuesta para un humano (título + tipo +
// precios), normalizado para que el mismo contenido dé siempre la misma clave.
export function proposalKey(o = {}) {
  const base = [
    String(o.title || "").toLowerCase().trim().replace(/\s+/g, " "),
    o.offer_type || "",
    o.regular_price ?? "",
    o.coupon_price ?? "",
    o.unit_price ?? "",
    o.discount_pct ?? "",
  ].join("|");
  // Hash corto y estable (djb2). No es criptográfico: solo necesita no colisionar
  // entre 3-12 tarjetas del mismo comercio.
  let h = 5381;
  for (let i = 0; i < base.length; i++) h = ((h << 5) + h + base.charCodeAt(i)) | 0;
  return "p" + (h >>> 0).toString(36);
}

/** Garantiza `_key` en cada propuesta, sin pisar una que ya venga. */
export function withKeys(offers = []) {
  return offers.map((o) => (o && o._key ? o : { ...o, _key: proposalKey(o) }));
}

// ---------------------------------------------------------------- el sobre
/**
 * El sobre que el front escribe en `last_advisor_json`.
 * Convive con el formato CRUDO que escriben offer_advisor / ingest_offer_ideas
 * server-side: el lector de abajo acepta los dos.
 */
export function buildDraftPayload({ offers = [], selectedKeys = [], generatedAt = "" }) {
  // Red de seguridad del techo: se aplica en TODO camino de escritura, no solo en
  // el merge. Así ninguna ruta futura puede escribir un sobre sin tope por olvido.
  const capped = capProposals(offers);
  const keys = new Set(capped.map((o) => o._key));
  return JSON.stringify({
    __tag: ENVELOPE_TAG,
    v: ENVELOPE_VERSION,
    generated_at: generatedAt || new Date().toISOString(),
    selected: (selectedKeys || []).filter((k) => keys.has(k)),
    offers: capped,
  });
}

function parseEnvelope(raw) {
  if (!raw) return null;
  try {
    const o = typeof raw === "object" ? raw : JSON.parse(String(raw));
    if (o && o.__tag === ENVELOPE_TAG && Array.isArray(o.offers)) return o;
  } catch (_) { /* no es nuestro sobre: cae al parseo crudo */ }
  return null;
}

// ---------------------------------------------------------------- lectura
/**
 * Lee el borrador desde el perfil del comercio.
 *
 * DEGRADACIÓN LIMPIA (requisito 4 del ticket): mientras `last_advisor_json` no
 * exista en Bubble —ni como campo ni en la whitelist de get_merchant_profile—
 * esto devuelve null y NO rompe nada. No es un error: es que todavía no hay nada
 * que leer. El día que el campo exista y vuelva en el perfil, empieza a hidratar
 * sin tocar React.
 *
 * NO CONSUME CUPO: es lectura del perfil que el componente ya carga. Ninguna
 * llamada al asesor.
 */
export function readAdvisorDraft(merchantProfile, lang = "es") {
  const raw = merchantProfile && merchantProfile.last_advisor_json;
  if (!raw || !String(raw).trim()) return null;

  // Caso 1: el sobre del front — trae la selección.
  const env = parseEnvelope(raw);
  if (env) {
    // Techo también en lectura: un sobre viejo escrito antes del tope no puede
    // reintroducir el peso por la puerta de atrás.
    const offers = capProposals(withKeys(env.offers));
    const keys = new Set(offers.map((o) => o._key));
    return {
      offers,
      selectedKeys: (env.selected || []).filter((k) => keys.has(k)),
      generatedAt: env.generated_at || "",
      source: "envelope",
    };
  }

  // Caso 2: advisor_json crudo escrito server-side (offer_advisor / el alta).
  // La generación sobrevivió pero nadie marcó nada todavía → selección vacía.
  const offers = withKeys(parseAdvisorRaw(raw, lang));
  if (!offers.length) return null;
  return { offers, selectedKeys: [], generatedAt: "", source: "raw" };
}

// ---------------------------------------------------------------- escritura
/**
 * Persiste el borrador. Remoto primero; el local solo si está habilitado, y
 * siempre COMO RESPALDO del remoto, nunca en su lugar.
 * Nunca lanza: devuelve dónde quedó guardado.
 */
export async function persistAdvisorDraft({ merchantId, offers, selectedKeys, generatedAt }) {
  const payload = buildDraftPayload({ offers, selectedKeys, generatedAt });
  const remote = await saveAdvisorDraft(payload);

  let local = false;
  if (LOCAL_BACKEND_ENABLED && merchantId) {
    try {
      window.localStorage.setItem(LOCAL_PREFIX + merchantId, payload);
      local = true;
    } catch (_) { /* cuota llena o modo privado: no es un error para el comercio */ }
  }
  return { remote: remote.ok, local, reason: remote.ok ? null : remote.reason };
}

// ---------------------------------------------------------------- fusión
/**
 * 🔴 GUARD ANTI-PISADA. `last_advisor_json` guarda UNA sola generación, así que
 * generar de nuevo pisaría lo que el comercio había marcado — la misma forma del
 * S3 que se cerró en s169 (pérdida silenciosa), por otro camino.
 *
 * La regla, decidida por Ali en s169:
 *   - las propuestas MARCADAS de generaciones anteriores se conservan;
 *   - las NO marcadas se descartan;
 *   - las nuevas se agregan al final, sin marcar.
 *
 * Conservar todo haría crecer la lista sin techo (tres generaciones = doce
 * tarjetas, casi todas viejas y sin marcar). Descartar las no marcadas acota la
 * lista y además ejecuta el punto 5 de s159-D3 — el descarte explícito.
 *
 * FALLA CERRADA: ante la duda no se pisa. Si la selección viene vacía o rara,
 * `keep` queda vacío y no se pierde nada marcado, porque no había nada marcado.
 */
export function mergeGenerations(prevOffers = [], prevSelectedKeys = [], incomingOffers = []) {
  const selected = new Set(prevSelectedKeys || []);
  const prev = withKeys(prevOffers);
  const incoming = withKeys(incomingOffers);

  const keep = prev.filter((o) => selected.has(o._key));
  const discarded = prev.length - keep.length;

  const seen = new Set(keep.map((o) => o._key));
  const fresh = incoming.filter((o) => !seen.has(o._key));

  // Techo (s169 agregado 1): las MARCADAS primero, las nuevas después, y lo que
  // sobra se cae. El orden importa — lo que el comercio eligió nunca se pierde
  // por rebalse; lo que se cae es siempre propuesta nueva que todavía no miró.
  const full = [...keep, ...fresh];
  const offers = capProposals(full);
  const overflowed = full.length - offers.length;

  return {
    offers,
    selectedKeys: keep.map((o) => o._key),   // lo marcado sigue marcado
    kept: keep.length,
    discarded,
    added: fresh.length - overflowed,
    overflowed,
  };
}
